/**
 * ============================================================================
 * useCreateSupplierMutation — Phase 6d frontend (Supplier Management).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: POST /api/v1/suppliers — create a PENDING_REQUEST supplier record.
 *          Server runs fuzzy-name dedup (SUP-IMP-002) and returns
 *          409 + suggestions when a probable match is found; the UI lets the
 *          user override by re-submitting with `confirm_no_duplicate: true`.
 * ============================================================================
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import { apiFetch, newIdempotencyKey } from '../../api/client';
import type {
  SupplierCriticality,
  SupplierSubtype,
  SupplierSummary,
} from './useSuppliersQuery';

export interface CreateSupplierInput {
  name: string;
  legal_name?: string;
  criticality: SupplierCriticality;
  subtype: SupplierSubtype;
  country_code?: string;
  vat_id?: string;
  site_code?: string;
  // SUP-FULL — full supplier profile fields (all persisted to real columns).
  contact_email?: string;
  contact_phone?: string;
  single_source?: boolean;
  risk_category?: SupplierCriticality;
  risk_score?: number;
  next_requalification_at?: string; // YYYY-MM-DD
  lifecycle_status?: string;
  /** Set true on retry after the user dismissed a fuzzy-match warning. */
  confirm_no_duplicate?: boolean;
}

export interface CreateSupplierResponse extends SupplierSummary {
  /** When confirm_no_duplicate is false, server may return likely duplicates. */
  duplicate_candidates?: Array<{
    supplier_id: string;
    name: string;
    similarity: number;
  }>;
}

export function useCreateSupplierMutation(): UseMutationResult<
  CreateSupplierResponse,
  Error,
  CreateSupplierInput
> {
  return useMutation<CreateSupplierResponse, Error, CreateSupplierInput>({
    mutationFn: (input) =>
      apiFetch<CreateSupplierResponse>('/api/v1/suppliers', {
        method: 'POST',
        body: input,
        idempotencyKey: newIdempotencyKey(),
      }),
  });
}
